import { Injectable } from 'angular-ts-decorators';

import { flow, orderBy, reduce, map } from 'lodash';
import { Translate } from './translate';
import { Domain } from './domain';
import { FileDownloader } from './file-downloader';
import type { IConfig } from './config';

export interface IRawSalesReportData {
  label: string;
  name: string;
  turnover: number;
}

/**
 * A service that exposes sales reports endpoints that were created for v7 to
 * v6
 */
@Injectable('salesReports')
export class SalesReports {
  private $translate: Translate;

  constructor(
    private $http: ng.IHttpService,
    translate: Translate,
    private fileDownloader: FileDownloader,
    private domain: Domain,
    private config: IConfig,
  ) {
    'ngInject';

    this.$translate = translate;
  }

  private baseUrl() {
    return '//' + this.domain.sibling(this.config.spynlSubdomain);
  }

  /**
   * Returns a data object with data from a turnover, sales report that can be downloaded by the user.
   * @param {string} resourceUri
   * Name of the resource you want to retrieve (per-warehouse, per-article, period)
   * @param {object} params
   * Object filled with the parameters needed for the report requests
   * @param {string} resourceType
   * Optional parameter if you want to retrieve it as a xlsx or csv report
   * @return {promise}
   * Promise for the data being fetched
   */
  private getSalesReport(
    resourceUri: string,
    params: object,
    resourceType?: string,
  ): ng.IPromise<any> {
    let options = {
      withCredentials: true,
      params: params,
    };

    if (resourceType === 'xlsx') {
      resourceUri = `${resourceUri}-excel`;
      angular.extend(options, { responseType: 'arraybuffer' });
    } else if (resourceType === 'csv') {
      resourceUri = `${resourceUri}-csv`;
      angular.extend(options, { responseType: 'arraybuffer' });
    } else if (resourceType === 'pdf') {
      resourceUri = `${resourceUri}-pdf`;
      angular.extend(options, { responseType: 'arraybuffer' });
    }

    return this.$http
      .get(this.baseUrl() + `/sales/${resourceUri}`, options)
      .then((response: any) => {
        if (resourceType) {
          return response.data;
        }

        return response.data.data;
      });
  }

  /**
   * @param {date} startDate
   * start of report period
   * @param {date} endDate
   * end of report period
   * Sends transaction to /sales/add
   * @return {promise}
   * It will be resolved with fetched data, or rejected with error
   * description. The data is returned directly form the spynl, so all
   * inconsistencies are originated there. We expect the array of objects:
   * <code><pre>
   * {
   *   warehouse: {
   *     id: "55", # String, the warehouse id
   *     name: "Den Haag" # String, the warehouse name
   *   },
   *   turnover: 1676.48 # Number, summarized sales over the period
   * }
   * </pre><code>
   */
  perWarehouse(startDate: Date, endDate: Date): ng.IPromise<any> {
    return this.getSalesReport('per-warehouse', {
      startDate: angular.isDate(startDate) ? startDate.toJSON() : startDate,
      endDate: angular.isDate(endDate) ? endDate.toJSON() : endDate,
    });
  }

  /**
   * Method that returns the transactions summarized per each article; plus
   * optionally filtered by the warehouse id.
   * Response structure:
   * <code><pre>
   * {
   *   code: "w2-gs-5620-70", // if present
   *   name: "G-Star Elwood Cord", // name of the category (article, category, or
   *                               // articleGroup)
   *   turnover: 166.95
   * }
   * </pre></code>
   * @param {date} startDate
   * End date of the period
   * @param {string} endDate
   * Start date of the period
   * @param {string} category
   * Optional parameter. Accepted values 'article', 'brand', or
   * 'articleGroup'. If not present will be defaulted by backend to 'article'.
   * @param {string} deviceId
   * Optional parameter. The Mongo _id of the device user
   * @return {promise}
   * Promise for the data being fetched
   */
  getPerCategory(
    startDate: Date,
    endDate: Date,
    category: string,
    deviceId?: string,
  ): ng.IPromise<any> {
    return this.getSalesReport('per-article', {
      startDate: angular.isDate(startDate) ? startDate.toJSON() : startDate,
      endDate: angular.isDate(endDate) ? endDate.toJSON() : endDate,
      category: category,
      device: deviceId,
    }).then((data) => {
      // TODO SWDASH-279 stop mapping data after is retuned in requested format
      return map(data, (value) => {
        const unknown = this.$translate.instant('shared.unknown');
        const mappedValue: any = {
          turnover: value.turnover,
        };

        if (angular.isDefined(value.article)) {
          mappedValue.name = value.article.description
            ? value.article.description
            : unknown;
          mappedValue.code = value.article.code ? value.article.code : unknown;
          mappedValue.qty = value.qty;
        } else if (angular.isDefined(value.articleColorSize)) {
          mappedValue.name = value.articleColorSize.description
            ? value.articleColorSize.description
            : unknown;
          mappedValue.code = value.articleColorSize.code
            ? value.articleColorSize.code
            : unknown;
          mappedValue.color = value.articleColorSize.color
            ? value.articleColorSize.color
            : unknown;
          mappedValue.sizeLabel = value.articleColorSize.sizeLabel
            ? value.articleColorSize.sizeLabel
            : unknown;
          mappedValue.qty = value.qty;
        } else if (angular.isDefined(value.brand)) {
          mappedValue.name = value.brand ? value.brand : unknown;
          mappedValue.qty = value.qty;
        } else if (angular.isDefined(value.articleGroup)) {
          mappedValue.name = value.articleGroup ? value.articleGroup : unknown;
          mappedValue.qty = value.qty;
        }

        return mappedValue;
      });
    });
  }

  /**
   * Method that returns the transactions summary for the warehouse
   * withing the period. Response structure:
   * <code><pre>
   * {
   *   _id: null, # returned by aggregation framework of mongo, meaningless
   *   items: 1, # total number of items in the period
   *   itemsPer: 1, # average number of items per a transactions
   *   returns: 0, # total number of returns
   *   totalPer: 10, # average total of transactions
   *   transactions: 1, # number of transactions
   *   turnover: 10 # total value of transactions
   * }
   * </pre></code>
   * @param {date} startDate
   * End date of the period
   * @param {string} endDate
   * Start date of the period
   * @param {integer|string} warehouseId
   * Id of the warehouse for which data should be requested
   * @param {string} deviceId
   * Optional parameter. The Mongo _id of the device user
   * @return {promise}
   * Promise for the data being fetched
   */
  getSummary(
    startDate: Date,
    endDate: Date,
    warehouseId?: string | number, // TODO define & validate with data schema
    deviceId?: string,
  ): ng.IPromise<any> {
    return this.getSalesReport('summary', {
      startDate: angular.isDate(startDate) ? startDate.toJSON() : startDate,
      endDate: angular.isDate(endDate) ? endDate.toJSON() : endDate,
      warehouseId: warehouseId,
      device: deviceId,
    });
  }

  /**
   * Process raw data and reduce extra data into a single data point
   * @param {array} data
   * raw sales data
   * @param {number} maxDataPoints
   * The number of data points to return. All other values will be
   * summed into a new property
   * @param {string} label
   * The name of the label for additional values
   * @return {promise}
   * Promise for the data being fetched
   */
  combineData(
    data: IRawSalesReportData[],
    maxDataPoints: number,
    label: string,
  ): IRawSalesReportData[] {
    let combinedData = angular.copy(data);
    maxDataPoints = angular.isNumber(maxDataPoints) ? maxDataPoints : 10;
    label = angular.isString(label) ? label : '';

    return <IRawSalesReportData[]>flow(
      (values) => orderBy(values, 'turnover', 'desc'),
      (values) =>
        reduce(
          values,
          (old, current) => {
            if (old.length < maxDataPoints) {
              old.push(current);
            } else {
              old[maxDataPoints - 1].turnover += current.turnover;
              old[maxDataPoints - 1].name = label;
              old[maxDataPoints - 1].code = label;
            }
            return old;
          },
          [],
        ),
    )(combinedData);
  }

  /**
   * Retrieves the per warehouse report and starts the download for the user
   * @param {string} downloadType
   * The file type you want the data to be formatted in (xlsx, csv)
   * @param {date} startDate
   * start of report period
   * @param {date} endDate
   * end of report period
   * @return {promise}
   * Promise for when the downloaded completed or failed
   */
  downloadPerWarehouseReport(
    downloadType: string,
    startDate: Date,
    endDate: Date,
  ): ng.IPromise<any> {
    return this.getSalesReport(
      'per-warehouse',
      {
        startDate: angular.isDate(startDate) ? startDate.toJSON() : startDate,
        endDate: angular.isDate(endDate) ? endDate.toJSON() : endDate,
      },
      downloadType,
    ).then((data) =>
      this.fileDownloader.createDownloadElement(
        'warehouse-report',
        data,
        downloadType,
      ),
    );
  }

  /**
   * Retrieves the per category report and starts the download for the user
   * @param {string} downloadType
   * The file type you want the data to be formatted in (xlsx, csv)
   * @param {date} startDate
   * End date of the period
   * @param {string} endDate
   * Start date of the period
   * @param {string} category
   * Optional parameter. Accepted values 'article', 'brand', or
   * 'articleGroup'. If not present will be defaulted by backend to 'article'.
   * @return {promise}
   * Promise for when the downloaded completed or failed
   */
  downloadPerCategoryReport(
    downloadType: string,
    startDate: Date,
    endDate: Date,
    category?: string,
  ): ng.IPromise<any> {
    return this.getSalesReport(
      'per-article',
      {
        startDate: angular.isDate(startDate) ? startDate.toJSON() : startDate,
        endDate: angular.isDate(endDate) ? endDate.toJSON() : endDate,
        category: category,
      },
      downloadType,
    ).then((data) =>
      this.fileDownloader.createDownloadElement(
        'article-report',
        data,
        downloadType,
      ),
    );
  }
}
